import React from 'react';
import { sortBy } from 'lodash-es';
import { func, string } from 'prop-types';
import ExtraInfo from './ExtraInfo';
import styles from './dimensions.module.css';
import { relativeCategoryTypeValues, surveySchemaPartType } from '../../../constants/types';
import { getTranslation } from '../../../i18n';
import { categoryNameTranslationKey } from '../../Editor/SurveyPartEditor/RelativeVariableFeedback';
import { replaceStudentPsychologistLink } from '../../../util';

const renderFeedback = (feedback, t, lang) => {
  let categoryName;
  let key;
  let feedbackTitle;
  let improvementTitle;
  if (feedback.relativeCategory) {
    categoryName = t(categoryNameTranslationKey(feedback.relativeCategory.type));
    key = feedback.relativeCategory.type;
    feedbackTitle = t('surveySchemaParts.dimensions.relativeFeedback.feedback');
    improvementTitle = t('surveySummary.improvementSuggestions');
  } else {
    categoryName = getTranslation(feedback.absoluteCategory.description, lang);
    key = feedback.absoluteCategory.value;
    feedbackTitle = t('surveySchemaParts.dimensions.absoluteFeedback.feedback');
    improvementTitle = t('surveySummary.interpretation');
  }

  const improvementSuggestionsHtml = replaceStudentPsychologistLink(feedback.improvementSuggestions, lang);

  return (
    <div className={styles.row} key={key}>
      <div className={styles.label}>
        <p>{t('surveySummary.title')}: {categoryName}</p>
      </div>
      <div className={styles.content}>
        <h3>{feedbackTitle}</h3>
        <div
          className={`${styles.feedback} rich-text`}
          dangerouslySetInnerHTML={// eslint-disable-line react/no-danger
            { __html: getTranslation(feedback.feedback, lang) }
          }
        />
        {feedback.improvementSuggestions
          && <h3>{improvementTitle}</h3>}
        {feedback.improvementSuggestions
          && (
          <div
            className={`${styles.improvementSuggestions} rich-text`}
            dangerouslySetInnerHTML={// eslint-disable-line react/no-danger
              { __html: improvementSuggestionsHtml }
            }
          />
          )}
      </div>
    </div>
  );
};

const sortFeedback = ({ relativeCategory }, i) => {
  if (relativeCategory) {
    return relativeCategoryTypeValues.indexOf(relativeCategory.type);
  }

  return i;
};

const renderCode = (text) => <span className={styles.code}>{text}</span>;
const renderQuestion = (text) => <span className={styles.question}>{text}</span>;

const renderDimension = (dimension, surveyPartCode, updatedCallback, t, lang) => {
  const questions = dimension.questions.map((question) =>
    (
      <li key={question.code}>
        {renderCode(question.code)} – {renderQuestion(getTranslation(question.text, lang))}
      </li>
    ));
  const feedbackTexts = sortBy(dimension.responseCategories, sortFeedback)
    .map((feedback) => renderFeedback(feedback, t, lang));

  return (
    <div className={styles.dimBox} key={dimension.code}>
      <h2 className={styles.dimensionTitle}>{getTranslation(dimension.name, lang)}</h2>
      <div className={styles.row}>
        <div className={styles.label}><p>{t('surveyParts.identifier')}</p></div>
        <div className={styles.content}><p>{renderCode(dimension.code)}</p></div>
      </div>
      <div className={styles.row}>
        <div className={styles.label}><p>{t('surveyParts.questions')}</p></div>
        <div className={styles.content}>
          <ul className={styles.questionList}>
            {questions}
          </ul>
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.label}><p>{t('common.description')}</p></div>
        <div
          className={`${styles.description} ${styles.content} rich-text`}
          dangerouslySetInnerHTML={// eslint-disable-line react/no-danger
            { __html: getTranslation(dimension.description, lang) }
          }
        />
      </div>
      {feedbackTexts}
      <ExtraInfo
        variableExtraInfo={dimension.variableExtraInfo ?? null}
        surveyPartCode={surveyPartCode}
        dimension={dimension}
        updatedCallback={updatedCallback}
      />
    </div>
  );
};

const Dimensions = ({
  translate, surveyPart, updatedCallback, lang
}) => {
  const dims = surveyPart.variables.map((dimension) => renderDimension(dimension, surveyPart.code, updatedCallback, translate, lang));

  return (
    <div>{dims}</div>
  );
};

Dimensions.propTypes = {
  surveyPart: surveySchemaPartType.isRequired,
  translate: func.isRequired,
  updatedCallback: func.isRequired,
  lang: string.isRequired
};

export default Dimensions;
